import { vvSettings } from "./common";

/**
 * Form field visibility and validation settings from Action Settings Center
 * These map to UserInformationSettingsModel on the backend
 */
export interface FormFieldSettings {
    // Personal Information Fields
    honorificVisible: boolean;
    honorificMandatory: boolean;
    firstNameMandatory: boolean;
    lastNameMandatory: boolean;
    suffixVisible: boolean;
    emailVisible: boolean;
    emailEditable: boolean;
    nameEditable: boolean;
    
    // Phone Fields
    phoneVisible: boolean;
    phoneNumberMandatory: boolean;
    phoneNumberEditable: boolean;
    faxVisible: boolean;
    faxEditable: boolean;
    mobileNumberVisible: boolean;
    
    // Home Address Section
    homeSectionVisible: boolean;
    homeAddressEditable: boolean;
    homeAddressMandatory: boolean;
    
    // Business Section
    businessSectionVisible: boolean;
    businessAddressVisible: boolean;
    businessAddressEditable: boolean;
    businessAddressMandatory: boolean;
    businessNameVisible: boolean;
    businessNameEditable: boolean;
    businessTitleVisible: boolean;
    titleEditable: boolean;
    titleMandatory: boolean;
    companyNameMandatory: boolean;
    
    // Custom Fields
    customFieldsVisible: boolean;
    
    // Remember Me
    showRememberMeCheckbox: boolean;
    rememberMeCheckedDefault: boolean;
    
    // Subscribe (Email)
    showSubscribeCheckbox: boolean;
    subscribeCheckedDefault: boolean;
    
    // SMS/Text Subscription
    showTextSubscribeCheckbox: boolean;
    textSubscribeCheckedDefault: boolean;
    textingSubscriptionText?: string;
    mobileSubscriptionMessage?: string; // Disclaimer text: "By providing your phone number..."

    // Association Info (for disclaimer message)
    associationDisplayName?: string;
    privacyPolicyLink?: string;
    termsAndConditionsLink?: string;
    privacyPolicyLinkText?: string;
    termsAndConditionsLinkText?: string;
    
    // UI Text
    submitButtonText?: string;
    subscriptionText?: string;
    subscriptionTextIdentified?: string; // Text for returning/remembered users
    userInformationSectionHeading?: string;
    homeInformationSectionHeading?: string;
    businessInformationSectionHeading?: string;
    otherInformationSectionHeading?: string;
    petitionTextSectionHeading?: string;
    
    // Validation
    hideRequiredMarker: boolean;
    
    // Country/International
    country: string;
    enableInternationalAddresses: boolean;
}

/**
 * Custom field types supported by the form
 */
export type CustomFieldAnswerType = 
    | 'Text'
    | 'Checkbox'
    | 'Radio'
    | 'Boolean'
    | 'Datepicker'
    | 'MultipleOptionsSingleAnswer'
    | 'MultipleOptionsMultipleAnswers';

/**
 * Custom field definition from the API
 */
export interface CustomField {
    id: number;
    questionType: string;
    answerType: CustomFieldAnswerType;
    isMandatoryWhenDisplayed: boolean;
    autoYesIfBoolean: boolean;
    displaySection: 'User' | 'Home' | 'Business' | 'Other';
    displayName: string;
    mappedUserFieldName?: string;
    instructions?: string;
    possibleAnswers?: string[];
    userCustomFieldValue?: string;
    isDefaultDisplaySection: boolean;
    isAdminReadOnly: boolean;
    showInstructionsAsDisplayName: boolean;
}

/**
 * Complete form configuration including field settings and custom fields
 */
export interface PetitionFormSettings {
    fieldSettings: FormFieldSettings;
    customFields: CustomField[];
}

/**
 * Default form field settings (used as fallback)
 */
export const defaultFormFieldSettings: FormFieldSettings = {
    // Personal Info - always show names and email
    honorificVisible: false,
    honorificMandatory: false,
    firstNameMandatory: true,
    lastNameMandatory: true,
    suffixVisible: false,
    emailVisible: true,
    emailEditable: true,
    nameEditable: true,
    
    // Phone - hidden by default
    phoneVisible: false,
    phoneNumberMandatory: false,
    phoneNumberEditable: true,
    faxVisible: false,
    faxEditable: true,
    mobileNumberVisible: false,
    
    // Home Address - visible by default
    homeSectionVisible: true,
    homeAddressEditable: true,
    homeAddressMandatory: false,
    
    // Business - hidden by default
    businessSectionVisible: false,
    businessAddressVisible: false,
    businessAddressEditable: true,
    businessAddressMandatory: false,
    businessNameVisible: false,
    businessNameEditable: true,
    businessTitleVisible: false,
    titleEditable: true,
    titleMandatory: false,
    companyNameMandatory: false,
    
    // Custom Fields
    customFieldsVisible: true,
    
    // Remember Me
    showRememberMeCheckbox: true,
    rememberMeCheckedDefault: true,
    
    // Subscribe (Email)
    showSubscribeCheckbox: false,
    subscribeCheckedDefault: false,
    
    // SMS/Text Subscription
    showTextSubscribeCheckbox: false,
    textSubscribeCheckedDefault: false,
    textingSubscriptionText: undefined,
    mobileSubscriptionMessage: "By providing your phone number, you agree to receive informational and advocacy text messages from {0} via VoterVoice. Msg & data rates may apply. Message frequency varies. Reply HELP for help. Reply STOP to opt out.",

    // Association Info
    associationDisplayName: undefined,
    privacyPolicyLink: undefined,
    termsAndConditionsLink: undefined,
    privacyPolicyLinkText: undefined,
    termsAndConditionsLinkText: undefined,
    
    // UI Text
    submitButtonText: undefined,
    subscriptionText: undefined,
    subscriptionTextIdentified: undefined,
    userInformationSectionHeading: undefined,
    homeInformationSectionHeading: 'Home Address',
    businessInformationSectionHeading: 'Business Information',
    otherInformationSectionHeading: 'Other Information',
    petitionTextSectionHeading: undefined,
    
    // Validation
    hideRequiredMarker: false,
    
    // Country
    country: 'US',
    enableInternationalAddresses: false
};

/**
 * Get form settings for petition signing
 * Reads from vvSettings (injected server-side) with fallback to defaults
 * 
 * This is similar to how electionCenter settings work - the server injects
 * the configuration into window.vvSettings when rendering the page.
 */
export const getPetitionFormSettings = (): PetitionFormSettings => {
    const serverSettings = vvSettings?.petitionFormSettings;
    
    if (serverSettings) {
        return {
            fieldSettings: {
                ...defaultFormFieldSettings,
                ...(serverSettings.fieldSettings || {})
            },
            customFields: serverSettings.customFields || []
        };
    }
    
    // Fallback to defaults if settings not injected
    // This allows the form to work during development/testing
    console.info('Petition form settings not found in vvSettings, using defaults');
    return {
        fieldSettings: defaultFormFieldSettings,
        customFields: []
    };
};

/**
 * Get US states for address dropdown
 */
export const getUSStates = (): string[] => [
    '', 'AK', 'AL', 'AR', 'AS', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'GU', 'HI',
    'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT',
    'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VA', 'VI', 'VT', 'WA', 'WI', 'WV', 'WY', 'AA', 'AE', 'AP'
];

/**
 * Get Canadian provinces for address dropdown
 */
export const getCanadianProvinces = (): string[] => [
    '', 'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
];

/**
 * Get states/provinces based on country
 */
export const getStatesForCountry = (country: string): string[] => {
    return country === 'CA' ? getCanadianProvinces() : getUSStates();
};

